import { GridRowId } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { useIntl } from "react-intl";
import { FilterType } from "../Alert/Filter/FilterType";
import {
  Filter,
  DateOptions,
  DimensionOptions,
  DateRangeOptions,
} from "../services/Alert";
import { getDates } from "../Alert/Filter/CreateFilterDateForm";
import {
  ExportParamsType,
  RequestHandlersType,
  RestrictionUpdateUpdate,
  SelectionModelType,
} from "./interfaces";
import {
  GenericObject,
  PriceUpdate,
  PriceUpdateStatus,
  basicFetch,
} from "../services/Price";
import { GridSectionsApiValues } from "../Calendar/interfaces";
import { checkErrors, getDefaultOptions } from "../services/Category";
import { buildDimensionFilters } from "../services/AlertListFilterPayload";
import { UseMutateAsyncFunction } from "react-query";
import { DateTime } from "luxon";

type StatusTranslationKeyField = { [key in PriceUpdateStatus]: any };

export const useGetStatus = () => {
  const intl = useIntl();
  const [status] = useState<StatusTranslationKeyField>({
    [PriceUpdateStatus.CLOSED]: intl.formatMessage({ id: "STATUS_CLOSED" }),
    [PriceUpdateStatus.ERROR]: intl.formatMessage({ id: "STATUS_ERROR" }),
    [PriceUpdateStatus.NOT_SENT]: `${intl.formatMessage({
      id: "STATUS_PROCESSED",
    })} (local)`,
    [PriceUpdateStatus.OUT_OF_RANGE]: intl.formatMessage({
      id: "STATUS_OUT_OF_RANGE",
    }),
    [PriceUpdateStatus.PENDING]: intl.formatMessage({ id: "STATUS_PENDING" }),
    [PriceUpdateStatus.PROCESSED]: intl.formatMessage({
      id: "STATUS_PROCESSED",
    }),
    [PriceUpdateStatus.PROCESSING]: intl.formatMessage({
      id: "STATUS_PROCESSING",
    }),
    [PriceUpdateStatus.REJECTED]: intl.formatMessage({ id: "STATUS_REJECTED" }),
    [PriceUpdateStatus.STALE]: intl.formatMessage({ id: "STATUS_STALE" }),
    [PriceUpdateStatus.UNMAPPED]: intl.formatMessage({ id: "STATUS_UNMAPPED" }),
    [PriceUpdateStatus.VALIDATED]: intl.formatMessage({
      id: "STATUS_VALIDATED",
    }),
  });
  return status;
};

export const useGetStatusList = () => {
  const statuses = useGetStatus();
  const [statusList] = useState<Array<{ name: string; label: string }>>(
    Object.entries(statuses).map(([name, label]) => ({ name, label }))
  );
  return statusList;
};

// Which end of the move a value column shows, and when. A row on its way out
// still shows the price it is leaving as its current one and the one it heads
// to as its target; once it has left, the new price IS the current one and the
// old moves one column left. Statuses in neither set (stale, unmapped) show
// nothing at all.
const AWAITING: string[] = [
  PriceUpdateStatus.PENDING,
  PriceUpdateStatus.ERROR,
  PriceUpdateStatus.REJECTED,
];
const SETTLED: string[] = [
  PriceUpdateStatus.PROCESSED,
  PriceUpdateStatus.NOT_SENT,
  PriceUpdateStatus.VALIDATED,
  PriceUpdateStatus.PROCESSING,
];

type MoveSide = "old" | "new";

/**
 * The end of the move a column shows for this row, or null when it shows none.
 *
 * The three value columns say different things at different statuses, and both
 * the amount and the ladder level of a cell follow from this single answer.
 */
export const sideOf = (row: GenericObject, field: string): MoveSide | null => {
  if (AWAITING.includes(row.status)) {
    if (field === "current_value") return "old";
    if (field === "new_value") return "new";
    return null;
  }
  if (SETTLED.includes(row.status)) {
    if (field === "old_value") return "old";
    if (field === "current_value") return "new";
  }
  return null;
};

// A restriction keeps its value under `old` / `new`; a price under
// `old_price_net` / `new_price_net`, which hold the tax-inclusive amount.
const valueOf = (row: GenericObject, side: MoveSide) => {
  if (row.type === GridSectionsApiValues.PRICE) {
    return row[`${side}_price_net`] || null;
  }
  const value = row[side];
  return value || value === 0 ? value : null;
};

const valueFor = (row: GenericObject, field: string) => {
  const side = sideOf(row, field);
  return side ? valueOf(row, side) : null;
};

/**
 * The grid level this cell’s price stood on, or null when it stood on none.
 *
 * Read from the level the move recorded and never re-derived from the amount:
 * a column may price two tiers the same, and the applied price lags a send
 * anyway. Null on a grid-priced cell means the price was not exactly a tier.
 */
export const tierLevelOf = (
  row: GenericObject,
  field: string
): number | null => {
  if (row.type !== GridSectionsApiValues.PRICE || !row.tier_grid) return null;
  const side = sideOf(row, field);
  if (side === null) return null;
  return row[`${side}_tier_level`] ?? null;
};

/** The price a row is leaving, shown once it has actually left. */
export const getOldValue = (row: GenericObject) => valueFor(row, "old_value");

/** Where the row stands: the old price while it waits, the new one after. */
export const getCurrentValue = (row: GenericObject) =>
  valueFor(row, "current_value");

/** Where the row is headed, shown only while it still needs an answer. */
export const getTargetValue = (row: GenericObject) =>
  valueFor(row, "new_value");

/**
 * Check date validity for date format "yyyy-MM-dd" and "yyyy-MM-dd HH:mm"
 */
function checkDateValidity(dateString: string) {
  let dt;
  if (dateString.length === 10) {
    dt = DateTime.fromFormat(dateString, "yyyy-MM-dd");
  } else {
    dt = DateTime.fromFormat(dateString, "yyyy-MM-dd HH:mm");
  }

  return dt;
}

/**
 * Generate Export Path
 *
 * At the begening we hardcode a list of all possible filters in the page and fill it with values in filters params.
 *
 * Note: We hardcode a list of all possible filters because we cannot generate dynamically this list, we don't have information about it
 */
export const generateExportPath = (filters: Filter[]) => {
  let exportUrl = "/api/workflow-updates/export/?";
  const scope: ExportParamsType = {
    occupation_date__gte: "",
    occupation_date__lte: "",
    uh_cat__property__in: [],
    uh_cat__property__statut_hebergement__in: [],
    uh_cat__property__nb_etoiles__in: [],
    uh_cat__property__rm_surqual__in: [],
    uh_cat__in: [],
    uh_cat__uh_type_nandc__in: [],
    updated_at__gte: "",
    updated_at__lte: "",
    status__in: [],
    type__in: [],
    analyst_fullname: [],
    owner__in: [],
    uh_cat__is_property_reference__in: [],
    uh_cat__is_uh_type_reference__in: [],
  };

  filters.forEach((filter) => {
    if (filter.type === FilterType.DATE) {
      const options = filter.options as DateOptions;

      const { start, end } = getDates(options);
      if (start.isValid && end.isValid) {
        scope.occupation_date__gte = start.toISODate();
        scope.occupation_date__lte = end.toISODate();
      } else {
        scope.occupation_date__gte = DateTime.now().toISODate();
        scope.occupation_date__lte = DateTime.now().toISODate();
      }
    } else if (filter.type === FilterType.DATE_RANGE) {
      const options = filter.options as DateRangeOptions;
      let startDate = undefined;
      let endDate = undefined;

      if (options.values[0]) {
        startDate = checkDateValidity(options.values[0]);
      }
      if (options.values[1]) {
        endDate = checkDateValidity(options.values[1]);
      }

      if (startDate && startDate.isValid && options.values[0]) {
        scope.updated_at__gte = options.values[0];
      }
      if (endDate && endDate.isValid && options.values[1]) {
        scope.updated_at__lte = options.values[1];
      }
    } else if (filter.type === FilterType.MULTIPLE) {
      const options = filter.options as DimensionOptions;

      switch (filter.target) {
        case 4:
          scope.uh_cat__property__in = options.values.map((value) => value.id);
          break;
        case 19:
          scope.uh_cat__property__statut_hebergement__in = options.values.map(
            (value) => value.label
          );
          break;
        case 10:
          scope.uh_cat__property__nb_etoiles__in = options.values.map(
            (value) => value.label
          );
          break;
        case 14:
          scope.uh_cat__property__rm_surqual__in = options.values.map(
            (value) => value.label
          );
          break;
        case 22:
          scope.uh_cat__in = options.values.map((value) => value.id);
          break;
        case 26:
          scope.uh_cat__uh_type_nandc__in = options.values.map(
            (value) => value.id
          );
          break;
        case 37:
          // (id dimension 37 = "Status de prix")
          // When price status filter is selected, translate status so we need
          // to use name param value to send to the backend instead of translated label
          scope.status__in = options.values.map((value) => value.name);
          break;
        case 39:
          scope.type__in = options.values.map((value) => value.label);
          break;
        case 1:
          scope.analyst_fullname = options.values.map((value) => value.label);
          break;
        case 38:
          scope.owner__in = options.values.map((value) => value.id);
          break;
        case 41:
          scope.uh_cat__is_property_reference__in = options.values.map(
            (value) => value.name
          );
          break;
        case 42:
          scope.uh_cat__is_uh_type_reference__in = options.values.map(
            (value) => value.name
          );
          break;
      }
    }
  });

  for (const [key, value] of Object.entries(scope)) {
    if (typeof value !== "string" && value.length > 0) {
      exportUrl += `${key}=${value.join(",")}&`;
    } else if (value !== "" && !Array.isArray(value)) {
      exportUrl += `${key}=${value}&`;
    }
  }

  return exportUrl;
};

export const UH_CAT_REFERENCE_FILTER_OPTIONS = [
  {
    id: 1,
    name: "True",
    label: "YES",
  },
  {
    id: 2,
    name: "False",
    label: "NO",
  },
];

export const PRICE_STATUS_FILTER_OPTIONS = [
  {
    id: 1,
    name: PriceUpdateStatus.ERROR,
    label: "STATUS_ERROR",
  },
  {
    id: 2,
    name: PriceUpdateStatus.PENDING,
    label: "STATUS_PENDING",
  },
  {
    id: 3,
    name: PriceUpdateStatus.PROCESSING,
    label: "STATUS_PROCESSING",
  },
  {
    id: 4,
    name: PriceUpdateStatus.PROCESSED,
    label: "STATUS_PROCESSED",
  },
  {
    id: 5,
    name: PriceUpdateStatus.REJECTED,
    label: "STATUS_REJECTED",
  },
  {
    id: 6,
    name: PriceUpdateStatus.STALE,
    label: "STATUS_STALE",
  },
  {
    id: 7,
    name: PriceUpdateStatus.VALIDATED,
    label: "STATUS_VALIDATED",
  },
];

export const retryRestrictionsBulk = async (restrictions: number[]) => {
  const url = new URL(`/api/restriction-updates/bulk-retry/`, window.origin);
  const body = { restrictions: restrictions };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

// The workflow page carries three families of rows; everything that is not a
// price used to be treated as a restriction, which sent yield-class ids to the
// restriction endpoints.
export const isYieldClassType = (type: GridSectionsApiValues) =>
  type === GridSectionsApiValues.YIELD_CLASS ||
  type === GridSectionsApiValues.YIELD_CLASS_PARENT ||
  type === GridSectionsApiValues.QUOTA;

const postYieldClassUpdates = async (action: string, ids: number[]) => {
  const url = new URL(`/api/yield-class-updates/${action}/`, window.origin);
  const body = { yield_class_updates: ids };

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "POST",
      body: JSON.stringify(body),
    })
  );

  checkErrors(response);

  return response.json();
};

export const validateYieldClassUpdates = (ids: number[]) =>
  postYieldClassUpdates("bulk-validate", ids);
export const rejectYieldClassUpdates = (ids: number[]) =>
  postYieldClassUpdates("bulk-reject", ids);
export const retryYieldClassUpdatesBulk = (ids: number[]) =>
  postYieldClassUpdates("bulk-retry", ids);

export const listWorkflowUpdates = async (params: any, filters?: Filter[]) => {
  const scope = filters ? buildDimensionFilters(filters, 2) : {};

  const options = {
    method: "POST",
    body: JSON.stringify({ scope }),
  };

  return basicFetch<PriceUpdate>(
    "/api/workflow-updates/grouped/",
    params,
    options
  );
};

// Use for update restiction (using restriction update id)
export const updateRestriction = async (
  restriction: RestrictionUpdateUpdate
) => {
  const url = new URL(
    `/api/restriction-updates/${restriction.id}/`,
    window.origin
  );

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "PUT",
      body: JSON.stringify(restriction),
    })
  );

  checkErrors(response);

  return response.json();
};

/**
 * Manage Mass actions (mass validate, mass reject, mass retry)
 *
 * Each row family has its own endpoint, so a mass action is up to three api
 * calls: prices, restrictions and yield classes.
 */
export const manageMassActionsRequests = (
  priceRequest: UseMutateAsyncFunction<any, unknown, GridRowId[], unknown>,
  restrictionRequest: UseMutateAsyncFunction<
    any,
    unknown,
    GridRowId[],
    unknown
  >,
  yieldClassRequest: UseMutateAsyncFunction<any, unknown, GridRowId[], unknown>,
  selectionModel: SelectionModelType[],
  priceRequestHandler: RequestHandlersType,
  restrictionRequestHandler: RequestHandlersType,
  yieldClassRequestHandler: RequestHandlersType
) => {
  const pricesID = selectionModel
    .filter((el) => el.type === GridSectionsApiValues.PRICE)
    .map((el) => el.id);

  const yieldClassesID = selectionModel
    .filter((el) => isYieldClassType(el.type))
    .map((el) => el.id);

  const restrictionsID = selectionModel
    .filter(
      (el) =>
        el.type !== GridSectionsApiValues.PRICE && !isYieldClassType(el.type)
    )
    .map((el) => el.id);

  const handlers = [
    priceRequestHandler,
    restrictionRequestHandler,
    yieldClassRequestHandler,
  ];

  // Use Promise.allSettled because if one api call failed this other can be succefull and we can generate differents notifications
  // When an array is empty, set false to Promise.allSettled to avoid fetching for nothing
  Promise.allSettled([
    pricesID.length > 0 ? priceRequest(pricesID) : false,
    restrictionsID.length > 0 ? restrictionRequest(restrictionsID) : false,
    yieldClassesID.length > 0 ? yieldClassRequest(yieldClassesID) : false,
  ]).then((results: any) => {
    results.forEach((el: GenericObject, index: number) => {
      const currRequestHandlers = handlers[index];
      if (!currRequestHandlers) {
        return;
      }

      if (
        el.status === "fulfilled" &&
        el.value &&
        currRequestHandlers.onSuccess
      ) {
        currRequestHandlers.onSuccess(el.value);
      } else if (currRequestHandlers.onError && el.reason) {
        currRequestHandlers.onError();
      }
    });

    // Use a random settled here for now, because all settled do same thing (refetch data table)
    priceRequestHandler.onSettled();
  });
};
